import type { FeatureFlagPort, LoggerPort } from "@tihany/platform-contracts";

const LOG_PREFIX = "[tihany-assist]";

const writeLog = (
  writer: (message?: unknown, ...optionalParams: unknown[]) => void,
  level: string,
  message: string,
  context?: Record<string, unknown>,
) => {
  if (context && Object.keys(context).length > 0) {
    writer(`${LOG_PREFIX} ${level} ${message}`, context);
    return;
  }
  writer(`${LOG_PREFIX} ${level} ${message}`);
};

export const appLoggerPort: LoggerPort = {
  debug(message, context) {
    writeLog(console.debug, "DEBUG", message, context);
  },
  info(message, context) {
    writeLog(console.info, "INFO", message, context);
  },
  warn(message, context) {
    writeLog(console.warn, "WARN", message, context);
  },
  error(message, context) {
    writeLog(console.error, "ERROR", message, context);
  },
};

const normalizeFlagKey = (flagKey: string) =>
  flagKey.trim().replace(/[^a-zA-Z0-9]+/g, "_").toUpperCase();

const readFlagToken = (flagKey: string): string | null => {
  const envKey = `VITE_FLAG_${normalizeFlagKey(flagKey)}`;
  const envValue = (import.meta.env as Record<string, unknown>)[envKey];
  if (typeof envValue !== "string") return null;
  const normalized = envValue.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

const ENABLED_FLAG_TOKENS = new Set(["1", "true", "on", "enabled", "yes"]);
const DISABLED_FLAG_TOKENS = new Set(["0", "false", "off", "disabled", "no"]);

export const appFeatureFlagPort: FeatureFlagPort = {
  isEnabled(flagKey) {
    const token = readFlagToken(flagKey);
    if (!token) return false;
    if (DISABLED_FLAG_TOKENS.has(token)) return false;
    return ENABLED_FLAG_TOKENS.has(token);
  },
  getVariant(flagKey) {
    const token = readFlagToken(flagKey);
    if (!token) return null;
    if (ENABLED_FLAG_TOKENS.has(token) || DISABLED_FLAG_TOKENS.has(token)) return null;
    return token;
  },
};
